/**
 * Authentication API Service
 */

import axios from 'axios';

// Use same logic as bomApi - detect environment and use appropriate base URL
const API_BASE_URL = process.env.REACT_APP_API_URL || 
  (window.location.hostname === 'localhost' ? 'http://localhost:8000' : window.location.origin);

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

export interface LoginResponse {
  success: boolean;
  token: string;
  must_change_password?: boolean;
  user: {
    id: string;
    email: string;
    username: string;
    full_name?: string;
    is_admin: boolean;
    is_superadmin: boolean;
    roles: Array<{
      id: string;
      name: string;
      display_name: string;
    }>;
    organizations?: Organization[];
    current_organization_id?: string | null;
    current_organization?: Organization | null;
  };
}

export interface Organization {
  id: string;
  name: string;
  display_name: string;
  theme: string;
  description?: string;
  logo_url?: string;
  is_current?: boolean;
  user_count?: number;
}

export interface UserResponse {
  id: string;
  email: string;
  username: string;
  full_name?: string | null;
  is_admin: boolean;
  is_superadmin: boolean;
  is_active: boolean;
  email_verified: boolean;
  roles: Array<{
    id: string;
    name: string;
    display_name: string;
  }>;
  organizations?: Organization[];
  current_organization_id?: string | null;
  current_organization?: Organization | null;
  created_at: string;
  last_login?: string | null;
}

export interface Role {
  id: string;
  name: string;
  display_name: string;
  description?: string;
  is_system_role: boolean;
  permissions: {
    can_upload_documents: boolean;
    can_extract: boolean;
    can_view_extractions: boolean;
    can_delete_documents: boolean;
    can_manage_users: boolean;
    can_manage_teams: boolean;
    can_view_billing: boolean;
    can_export_data: boolean;
  };
}

export interface Team {
  id: string;
  name: string;
  description?: string;
  member_count: number;
  created_at: string;
}

export interface TeamDetails {
  id: string;
  name: string;
  description?: string;
  created_by: string;
  members: Array<{
    id: string;
    username: string;
    email: string;
    full_name?: string;
    roles: Array<{
      id: string;
      name: string;
      display_name: string;
    }>;
  }>;
  created_at: string;
}

export interface TeamInvite {
  id: string;
  email: string;
  status: string;
  created_at: string;
  expires_at: string;
}

/**
 * Login
 */
export const login = async (usernameOrEmail: string, password: string): Promise<LoginResponse> => {
  const response = await api.post<LoginResponse>('/api/auth/login', {
    username_or_email: usernameOrEmail,
    password,
  });
  return response.data;
};

/**
 * Logout
 */
export const logout = async (token: string): Promise<void> => {
  await api.post('/api/auth/logout', {}, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
};

/**
 * Get current user
 */
export const getCurrentUser = async (token: string): Promise<UserResponse> => {
  const response = await api.get<UserResponse>('/api/auth/me', {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response.data;
};

/**
 * List all users
 */
export const listUsers = async (token: string): Promise<{ users: UserResponse[]; total: number }> => {
  const response = await api.get('/api/auth/users', {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response.data;
};

/**
 * Create user
 */
export const createUser = async (
  token: string,
  userData: {
    email: string;
    username: string;
    password?: string;
    full_name?: string;
    is_admin?: boolean;
    role_ids?: string[];
    organization_ids?: string[];
  }
): Promise<{ success: boolean; user: UserResponse; temporary_password?: string; must_change_password?: boolean }> => {
  const response = await api.post('/api/auth/users', userData, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response.data;
};

/**
 * List all roles
 */
export const listRoles = async (): Promise<{ roles: Role[] }> => {
  const response = await api.get('/api/auth/roles');
  return response.data;
};

/**
 * List teams
 */
export const listTeams = async (token: string): Promise<{ teams: Team[] }> => {
  const response = await api.get('/api/auth/teams', {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response.data;
};

/**
 * Get team details
 */
export const getTeam = async (token: string, teamId: string): Promise<{ team: TeamDetails }> => {
  const response = await api.get(`/api/auth/teams/${teamId}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response.data;
};

/**
 * Create team
 */
export const createTeam = async (
  token: string,
  teamData: { name: string; description?: string }
): Promise<{ success: boolean; team: Team }> => {
  const response = await api.post('/api/auth/teams', teamData, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response.data;
};

/**
 * Invite user to team
 */
export const inviteToTeam = async (
  token: string,
  teamId: string,
  inviteData: { email: string; role_id?: string }
): Promise<{ success: boolean; invite: any }> => {
  const response = await api.post(`/api/auth/teams/${teamId}/invite`, inviteData, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response.data;
};

/**
 * List team invites
 */
export const listTeamInvites = async (
  token: string,
  teamId: string
): Promise<{ invites: TeamInvite[] }> => {
  const response = await api.get(`/api/auth/teams/${teamId}/invites`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response.data;
};

/**
 * Delete user
 */
export const deleteUser = async (
  token: string,
  userId: string
): Promise<{ success: boolean; message: string }> => {
  const response = await api.delete(`/api/auth/users/${userId}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response.data;
};

/**
 * Change password
 */
export const changePassword = async (
  token: string,
  oldPassword: string,
  newPassword: string
): Promise<{ success: boolean; message: string }> => {
  const response = await api.post(
    '/api/auth/change-password',
    {
      old_password: oldPassword,
      new_password: newPassword,
    },
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
  return response.data;
};

/**
 * Forgot password
 */
export const forgotPassword = async (
  email: string
): Promise<{ success: boolean; message: string }> => {
  const response = await api.post('/api/auth/forgot-password', {
    email,
  });
  return response.data;
};

/**
 * Reset password
 */
export const resetPassword = async (
  token: string,
  newPassword: string
): Promise<{ success: boolean; message: string }> => {
  const response = await api.post('/api/auth/reset-password', {
    token,
    new_password: newPassword,
  });
  return response.data;
};

/**
 * Update profile
 */
export const updateProfile = async (
  token: string,
  profileData: {
    full_name?: string;
    profile_image_url?: string;
  }
): Promise<{ success: boolean; user: any }> => {
  const response = await api.put(
    '/api/auth/profile',
    profileData,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
  return response.data;
};

/**
 * Get user's organizations
 */
export const getOrganizations = async (token: string): Promise<{ success: boolean; organizations: Organization[]; current_organization_id?: string }> => {
  const response = await api.get('/api/auth/organizations', {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response.data;
};

/**
 * Get public organizations (for login page)
 */
export const getPublicOrganizations = async (): Promise<{ success: boolean; organizations: Organization[] }> => {
  const response = await api.get('/api/auth/organizations/public');
  return response.data;
};

/**
 * Get all organizations (PPCandaAdmin only)
 */
export const getAllOrganizations = async (token: string): Promise<{ success: boolean; organizations: Organization[] }> => {
  const response = await api.get('/api/auth/organizations/all', {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response.data;
};

/**
 * Switch organization
 */
export const switchOrganization = async (
  token: string,
  organizationId: string
): Promise<{ success: boolean; message: string; organization: Organization }> => {
  const response = await api.post(
    `/api/auth/organizations/${organizationId}/switch`,
    {},
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
  return response.data;
};

/**
 * Create organization (PPCandaAdmin only)
 */
export const createOrganization = async (
  token: string,
  orgData: {
    name: string;
    display_name: string;
    description?: string;
    theme: string;
    logo_url?: string;
  }
): Promise<{ success: boolean; organization: Organization }> => {
  const response = await api.post('/api/auth/organizations', orgData, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response.data;
};

/**
 * Update organization (PPCandaAdmin only)
 */
export const updateOrganization = async (
  token: string,
  organizationId: string,
  orgData: {
    display_name?: string;
    description?: string;
    theme?: string;
    logo_url?: string;
  }
): Promise<{ success: boolean; organization: Organization }> => {
  const response = await api.put(`/api/auth/organizations/${organizationId}`, orgData, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response.data;
};/**
 * Reset user password (PPCandaAdmin only)
 */
export const resetUserPassword = async (
  token: string,
  userId: string,
  autoGenerate: boolean = true,
  sendEmail: boolean = true
): Promise<{ success: boolean; message: string; temporary_password?: string; email_sent: boolean }> => {
  const response = await api.post(
    `/api/auth/users/${userId}/reset-password`,
    {
      auto_generate: autoGenerate,
      send_email: sendEmail,
    },
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
  return response.data;
};
/**
 * Update user's organizations (superadmin only)
 */
export const updateUserOrganizations = async (
  token: string,
  userId: string,
  organizationIds: string[]
): Promise<{ success: boolean; message: string; organizations: Organization[] }> => {
  const response = await api.put(
    `/api/auth/users/${userId}/organizations`,
    { organization_ids: organizationIds },
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
  return response.data;
};/**
 * Add user to organization (superadmin only)
 */
export const addUserToOrganization = async (
  token: string,
  userId: string,
  organizationId: string
): Promise<{ success: boolean; message: string }> => {
  const response = await api.post(
    `/api/auth/users/${userId}/organizations/${organizationId}`,
    {},
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
  return response.data;
};/**
 * Remove user from organization (superadmin only)
 */
export const removeUserFromOrganization = async (
  token: string,
  userId: string,
  organizationId: string
): Promise<{ success: boolean; message: string }> => {
  const response = await api.delete(
    `/api/auth/users/${userId}/organizations/${organizationId}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
  return response.data;
};/**
 * Delete team (admin only)
 */
export const deleteTeam = async (token: string, teamId: string): Promise<{ success: boolean; message: string }> => {
  const response = await api.delete(`/api/auth/teams/${teamId}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response.data;
};

export interface OpenRouterCredits {
  available: boolean;
  error?: string | null;
  label?: string | null;
  is_free_tier?: boolean;
  total_credits?: number | null;
  total_usage?: number | null;
  remaining?: number | null;
  key_limit?: number | null;
  key_limit_remaining?: number | null;
  key_limit_reset?: string | null;
  key_usage?: number | null;
  usage_daily?: number | null;
  usage_weekly?: number | null;
  usage_monthly?: number | null;
}

export interface OpenRouterSettings {
  configured: boolean;
  key_preview: string | null;
  llm_enabled: boolean;
  llm_model?: string;
  credits?: OpenRouterCredits | null;
}

export const getOpenRouterSettings = async (token: string): Promise<OpenRouterSettings> => {
  const response = await api.get('/api/settings/openrouter', {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response.data;
};

export const updateOpenRouterSettings = async (
  token: string,
  payload: { api_key?: string; llm_enabled?: boolean }
): Promise<OpenRouterSettings> => {
  const response = await api.put('/api/settings/openrouter', payload, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response.data;
};

export interface ErpEndpointMapping {
  key: string;
  label: string;
  method: string;
  demo_url: string;
  live_url: string;
  active_url: string;
  shared: boolean;
  note: string;
}

export interface ErpSettings {
  configured: boolean;
  key_preview: string | null;
  mode: 'demo' | 'live';
  base: string;
  endpoints: ErpEndpointMapping[];
}

export interface ErpTarget {
  mode: 'demo' | 'live';
  label: string;
  endpoints: { key: string; label: string; method: string; url: string }[];
}

export const getErpSettings = async (token: string): Promise<ErpSettings> => {
  const response = await api.get('/api/settings/erp', {
    headers: { Authorization: `Bearer ${token}` },
  });
  return response.data;
};

export const getErpTarget = async (token: string): Promise<ErpTarget> => {
  const response = await api.get('/api/settings/erp/target', {
    headers: { Authorization: `Bearer ${token}` },
  });
  return response.data;
};

export const updateErpSettings = async (
  token: string,
  payload: {
    api_key?: string;
    mode?: 'demo' | 'live';
    endpoints?: { key: string; demo_url: string; live_url: string }[];
  }
): Promise<ErpSettings> => {
  const response = await api.put('/api/settings/erp', payload, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return response.data;
};
